import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { User, LogOut, ShoppingBag, Sparkles, ShieldCheck, Shirt } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  useSeo("My Account — Collins Crafty Creations", "Sign in or create your Collins Crafty Creations account to shop, track orders, and manage custom requests.");
  const { user, loading } = useAuth();
  if (loading) return <div className="p-24 text-center text-navy/50" data-testid="account-loading">Checking session…</div>;
  if (user && user.role === "admin") {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="account-admin">
        <h1 className="font-serif text-3xl text-navy-dark">You're signed in as Admin</h1>
        <Link to="/admin/dashboard" className="mt-6 inline-block rounded-md bg-navy-dark text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.15em]">Go to Dashboard</Link>
      </div>
    );
  }
  return user ? <Portal /> : <AuthForms />;
}

function AuthForms() {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [params] = useSearchParams();
  const next = params.get("next") || "/account";
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [lf, setLf] = useState({ email: "", password: "" });
  const [rf, setRf] = useState({ name: "", email: "", password: "", phone: "", address: "" });

  const doLogin = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    const r = await login(lf.email, lf.password);
    setSubmitting(false);
    if (r.ok) { toast.success("Welcome back!"); navigate(next); } else setError(r.error);
  };
  const doRegister = async (e) => {
    e.preventDefault(); setError(""); setSubmitting(true);
    const r = await register(rf);
    setSubmitting(false);
    if (r.ok) { toast.success("Account created!"); navigate(next); } else setError(r.error);
  };

  return (
    <section className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="account-auth">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 sm:p-10 shadow-xl border border-silver-light">
        <div className="w-14 h-14 rounded-2xl bg-navy-dark text-white flex items-center justify-center mx-auto"><User size={22} /></div>
        <h1 className="font-serif text-3xl text-navy-dark text-center mt-5">{tab === "login" ? "Sign In" : "Create Your Account"}</h1>
        <p className="text-sm text-navy/60 text-center mt-1">Your Collins Crafty Creations account</p>

        <div className="mt-6 grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-full text-sm font-semibold">
          <button onClick={() => { setTab("login"); setError(""); }} data-testid="tab-login" className={`py-2 rounded-full transition-colors ${tab === "login" ? "bg-navy-dark text-white" : "text-navy/60"}`}>Sign In</button>
          <button onClick={() => { setTab("register"); setError(""); }} data-testid="tab-register" className={`py-2 rounded-full transition-colors ${tab === "register" ? "bg-navy-dark text-white" : "text-navy/60"}`}>Register</button>
        </div>

        {error && <div className="mt-4 text-sm text-cpr text-center" data-testid="auth-error">{error}</div>}

        {tab === "login" ? (
          <form onSubmit={doLogin} className="mt-6 space-y-4" data-testid="login-form">
            <div><Label>Email</Label><Input required type="email" value={lf.email} onChange={(e) => setLf({ ...lf, email: e.target.value })} data-testid="login-email" /></div>
            <div><Label>Password</Label><Input required type="password" value={lf.password} onChange={(e) => setLf({ ...lf, password: e.target.value })} data-testid="login-password" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-6 uppercase tracking-[0.15em] text-xs font-bold" data-testid="login-submit">{submitting ? "Signing in…" : "Sign In"}</Button>
          </form>
        ) : (
          <form onSubmit={doRegister} className="mt-6 space-y-4" data-testid="register-form">
            <div><Label>Full name</Label><Input required value={rf.name} onChange={(e) => setRf({ ...rf, name: e.target.value })} data-testid="reg-name" /></div>
            <div><Label>Email</Label><Input required type="email" value={rf.email} onChange={(e) => setRf({ ...rf, email: e.target.value })} data-testid="reg-email" /></div>
            <div><Label>Password <span className="text-navy/40">(min 8 chars)</span></Label><Input required type="password" minLength={8} value={rf.password} onChange={(e) => setRf({ ...rf, password: e.target.value })} data-testid="reg-password" /></div>
            <div><Label>Phone</Label><Input required value={rf.phone} onChange={(e) => setRf({ ...rf, phone: e.target.value })} data-testid="reg-phone" placeholder="(901) 555-0123" /></div>
            <div><Label>Mailing address</Label><Textarea required rows={2} value={rf.address} onChange={(e) => setRf({ ...rf, address: e.target.value })} data-testid="reg-address" placeholder="Street, City, State, ZIP" /></div>
            <Button type="submit" disabled={submitting} className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-6 uppercase tracking-[0.15em] text-xs font-bold" data-testid="reg-submit">{submitting ? "Creating…" : "Create Account"}</Button>
          </form>
        )}
      </div>
    </section>
  );
}

function Portal() {
  const { user, logout, updateProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [custom, setCustom] = useState([]);
  const [studio, setStudio] = useState([]);
  const [profile, setProfile] = useState({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    api.get("/me/orders").then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/me/custom-orders").then(({ data }) => setCustom(data)).catch(() => {});
    api.get("/me/custom-studio").then(({ data }) => setStudio(data)).catch(() => {});
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault(); setSavingProfile(true);
    const r = await updateProfile(profile);
    setSavingProfile(false);
    r.ok ? toast.success("Profile updated") : toast.error(r.error);
  };

  const Section = ({ title, Icon, items, render, testid }) => (
    <div className="rounded-2xl bg-white border border-silver-light p-6" data-testid={testid}>
      <div className="flex items-center gap-2 text-navy-dark"><Icon size={18} className="text-navy-royal" /><h3 className="font-serif text-xl">{title}</h3><span className="ml-auto text-xs text-navy/50">{items.length}</span></div>
      <div className="mt-4 space-y-2">
        {items.length === 0 ? <div className="text-sm text-navy/50">Nothing here yet.</div> : items.map(render)}
      </div>
    </div>
  );

  return (
    <section className="max-w-6xl mx-auto px-6 py-16" data-testid="account-portal">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.25em] uppercase text-navy-royal font-semibold">Client Portal</div>
          <h1 className="font-serif text-4xl text-navy-dark mt-1">Welcome, {user.name?.split(" ")[0]}.</h1>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-2 text-sm font-semibold text-cpr hover:opacity-80" data-testid="portal-logout"><LogOut size={16} /> Sign out</button>
      </div>

      <div className="mt-8 grid lg:grid-cols-[360px_1fr] gap-8">
        <form onSubmit={saveProfile} className="rounded-2xl bg-white border border-silver-light p-6 h-fit" data-testid="profile-form">
          <div className="flex items-center gap-2 text-navy-dark"><ShieldCheck size={18} className="text-navy-royal" /><h3 className="font-serif text-xl">My Profile</h3></div>
          <div className="mt-4 space-y-3">
            <div><Label>Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="pf-name" /></div>
            <div><Label>Email</Label><Input value={user.email} disabled className="bg-slate-50" data-testid="pf-email" /></div>
            <div><Label>Phone</Label><Input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} data-testid="pf-phone" /></div>
            <div><Label>Address</Label><Textarea rows={2} value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} data-testid="pf-address" /></div>
            <Button type="submit" disabled={savingProfile} className="w-full bg-navy-dark hover:bg-navy-royal text-white rounded-md py-5 uppercase tracking-[0.15em] text-xs font-bold" data-testid="pf-save">{savingProfile ? "Saving…" : "Save Profile"}</Button>
          </div>
        </form>

        <div className="grid sm:grid-cols-2 gap-6">
          <Section title="Store Orders" Icon={ShoppingBag} items={orders} testid="portal-orders" render={(o) => (
            <div key={o.session_id} className="text-sm border-b border-silver-light pb-2">
              <div className="font-medium text-navy-dark">${o.amount?.toFixed(2)} · <span className="capitalize">{o.payment_status}</span></div>
              <div className="text-xs text-navy/60">{(o.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</div>
            </div>
          )} />
          <Section title="Custom Orders" Icon={Sparkles} items={custom} testid="portal-custom" render={(c) => (
            <div key={c.id} className="text-sm border-b border-silver-light pb-2">
              <div className="font-medium text-navy-dark">{c.project_type} · qty {c.quantity}</div>
              <div className="text-xs text-navy/60 capitalize">{c.status}</div>
            </div>
          )} />
          <Section title="Custom Studio Orders" Icon={Shirt} items={studio} testid="portal-studio" render={(o) => (
            <div key={o.id} className="text-sm border-b border-silver-light pb-2 flex items-center justify-between gap-2">
              <div>
                <div className="font-medium text-navy-dark">{o.color} {o.size} × {o.quantity}</div>
                <div className="text-xs text-navy/60 capitalize">{o.price ? `$${o.price.toFixed(2)} · ` : ""}{o.status}</div>
              </div>
              {o.status === "invoiced" && o.invoice_url && (
                <a href={o.invoice_url} target="_blank" rel="noreferrer" className="shrink-0 rounded-md bg-navy-dark text-white px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide hover:bg-navy-royal" data-testid={`pay-invoice-${o.id}`}>Pay Invoice</a>
              )}
            </div>
          )} />
        </div>
      </div>
    </section>
  );
}
